/**
 * User-facing labels for the Asset library (the "Assets Hub").
 *
 * "asset" is the FROZEN internal codename — routes, API paths, types, query
 * keys all use it permanently. The product-facing NAME is not yet landed (it
 * may become "Documents" / "Document Library" / "Resource Hub") and BDMs risk
 * confusing it with infrastructure "assets". So every user-visible string for
 * this feature lives HERE: a rename is a one-file edit, no route/schema/test
 * churn. Same separation the repo already trusts for role labels
 * (see [[feedback_role_labels_via_module]] / role-labels.ts).
 *
 * To rename the feature, edit FEATURE_* below — nothing else.
 */

import type { Audience, AssetStatus, DocumentType } from "@/api/client";

// ---- Feature name (the part the product owner may rename) -----------------
export const FEATURE_NAV_LABEL = "Knowledge Base";

/**
 * Hub hero copy, role-conditional (CHP-423). Managers (RM/RA — admin, who can
 * upload/manage) get the "ready to manage" framing; everyone else (CM/PO/BDM)
 * gets the viewer "ready to go" framing.
 */
export function hubCopy(isAdmin: boolean): { title: string; subtitle: string } {
  return isAdmin
    ? {
        title: "Resources, ready to manage.",
        subtitle:
          "Oversee the library of assets and client-facing materials to enable partners and drive performance.",
      }
    : {
        title: "Resources, ready to go.",
        subtitle:
          "Access our library of assets and client-facing materials to build your knowledge and support your sales.",
      };
}

export const FEATURE_EMPTY_MESSAGE = "No resources match your filters yet.";

// Shown when no search/filter is active and the library itself is empty — a
// distinct first-run zero-state, not a zero-result-from-filters message.
export const FEATURE_LIBRARY_EMPTY_MESSAGE = "No resources yet — add the first one.";

// ---- Audience -------------------------------------------------------------
export const AUDIENCE_LABEL: Record<Audience, string> = {
  my_learning: "Enablement",
  for_clients: "Client Facing",
};

/**
 * CHP-959: audience-keyed colour band on the resource card — the designer's
 * primary ask, to tell enablement (internal) from client-facing material at a
 * glance. `band` is the thin solid left accent that carries the signal; `strip`
 * tints the icon rail behind it and `label` tints the audience eyebrow to
 * match. Two NON-evaluative hues so neither audience reads as good/bad: the
 * info (blue) lane for internal enablement, the brand coral for the outward,
 * client-facing face. All from existing semantic tokens (no raw palette).
 */
export const AUDIENCE_BAND: Record<Audience, { band: string; strip: string; label: string }> = {
  my_learning: {
    band: "border-info",
    strip: "bg-info-muted text-info-muted-foreground",
    label: "text-info-muted-foreground",
  },
  for_clients: {
    band: "border-primary",
    strip: "bg-brand-tint text-primary",
    label: "text-brand-tint-foreground",
  },
};

/**
 * CHP-959 (secondary): a very slight, categorical colour cue per file format —
 * the designer's "can we try…" exploration. Deliberately drawn from the
 * NON-evaluative tints (info / brand / accent) only — never the success or
 * warning lanes — so a format never reads as good/bad. Keyed on the canonical
 * `format_label` values (apps/api schemas/asset.py); unknown formats fall
 * through to the neutral outline badge. Subtle by design; confirm at sign-off.
 */
export const FORMAT_TINT: Record<string, string> = {
  PDF: "border-info-border bg-info-muted text-info-muted-foreground",
  PPT: "border-brand-tint-border bg-brand-tint text-brand-tint-foreground",
  PPTX: "border-brand-tint-border bg-brand-tint text-brand-tint-foreground",
  DOC: "border-accent-border bg-accent text-accent-foreground",
  DOCX: "border-accent-border bg-accent text-accent-foreground",
};

// ---- Document type --------------------------------------------------------
// Plural for the filter pills ("1-Pagers (3)"); singular for a single card's
// type chip ("1-Pager").
export const DOCUMENT_TYPE_PLURAL: Record<DocumentType, string> = {
  one_pager: "1-Pagers",
  battlecard: "Battlecards",
  slide_deck: "Slide Decks",
  case_study: "Case Studies",
};

export const DOCUMENT_TYPE_SINGULAR: Record<DocumentType, string> = {
  one_pager: "1-Pager",
  battlecard: "Battlecard",
  slide_deck: "Slide Deck",
  case_study: "Case Study",
};

// ---- Lifecycle status (admin-facing badge) --------------------------------
export const ASSET_STATUS_LABEL: Record<AssetStatus, string> = {
  draft: "Draft",
  published: "Published",
  archived: "Archived",
};
